import { useQueryClient } from '@tanstack/react-query';

import { useCancelReason } from '../api/get-cancel-reason';

interface UseCancelReasonDataProps {
  page: number;
  size: number;
}

export const useCancelReasonData = (queryString: UseCancelReasonDataProps) => {
  const queryClient = useQueryClient();

  const { cancelReasonData } = useCancelReason(queryString);

  // Enhanced refetch with cache invalidation
  const enhancedRefetch = async () => {
    await queryClient.invalidateQueries({ queryKey: ['CancelReason'] });
  };

  // Get cancel reason by ID
  const getCancelReasonById = (id: string): string => {
    if (!cancelReasonData?.cancelReasonMap[id]) {
      return '-';
    }
    return cancelReasonData.cancelReasonMap[id].reason;
  };

  // Check if cancel reason exists
  const hasCancelReason = (id: string): boolean => {
    return id in (cancelReasonData?.cancelReasonMap || {});
  };

  // Get all cancel reason options
  const getCancelReasonOptions = () => {
    if (!cancelReasonData?.cancelReasonMap) {
      return [];
    }

    return Object.entries(cancelReasonData.cancelReasonMap).map(
      ([id, reason]) => ({
        label: reason.reason,
        value: id,
      })
    );
  };

  // Get cancel reason options for specific IDs
  const getCancelReasonOptionsForIds = (ids: string[]) => {
    if (!cancelReasonData?.cancelReasonMap) {
      return [];
    }

    return ids
      .filter((id) => hasCancelReason(id))
      .map((id) => ({
        label: getCancelReasonById(id),
        value: id,
      }));
  };

  // Get cancel reason count
  const getCancelReasonCount = (): number => {
    return Object.keys(cancelReasonData?.cancelReasonMap || {}).length;
  };

  return {
    // Original data
    cancelReasonData,

    // Enhanced actions
    refetch: enhancedRefetch,

    // Utility functions
    getCancelReasonById,
    hasCancelReason,
    getCancelReasonOptions,
    getCancelReasonOptionsForIds,
    getCancelReasonCount,

    // Computed values
    isLoaded: !!cancelReasonData,
    hasData: getCancelReasonCount() > 0,
    isEmpty: !!cancelReasonData && getCancelReasonCount() === 0,
  };
};
